import {
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

import {
  ApplicationSurface,
} from "../../../components/application";
import {
  authorizedExternalProjectLogo,
  projectParticipationContextLabel,
} from "../utils/projectPresentation";
import ProjectCardPresentation from "./ProjectCardPresentation";

export default function ProjectParticipationCard({ project }) {
  const descriptionId = useId();
  const descriptionRef = useRef(null);
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const [descriptionOverflows, setDescriptionOverflows] = useState(false);

  useLayoutEffect(() => {
    const description = descriptionRef.current;
    if (!description || descriptionExpanded) return undefined;

    const measureOverflow = () => {
      setDescriptionOverflows(
        description.scrollHeight > description.clientHeight + 1
      );
    };

    measureOverflow();

    if (typeof ResizeObserver === "function") {
      const observer = new ResizeObserver(measureOverflow);
      observer.observe(description);
      return () => observer.disconnect();
    }

    window.addEventListener("resize", measureOverflow);
    return () => window.removeEventListener("resize", measureOverflow);
  }, [descriptionExpanded, project?.description]);

  if (!project?.slug) return null;

  const logo = authorizedExternalProjectLogo(project);
  const contextLabel = projectParticipationContextLabel(project);

  return (
    <ApplicationSurface
      as="article"
      className="av-project-participation-card"
      variant="nested"
    >
      <div className="av-project-participation-card__content">
        <ProjectCardPresentation
          contextLabel={contextLabel}
          headingLevel="h4"
          logo={logo}
          title={project.title}
        />
        {project.participantIdentifier ? (
          <p className="av-project-participation-card__participant">
            Participant ID: <strong>{project.participantIdentifier}</strong>
          </p>
        ) : null}
        {project.description ? (
          <div className="av-project-participation-card__description-region">
            <p
              className={[
                "av-project-participation-card__description",
                descriptionExpanded
                  ? "av-project-participation-card__description--expanded"
                  : "",
              ].filter(Boolean).join(" ")}
              id={descriptionId}
              ref={descriptionRef}
            >
              {project.description}
            </p>
            {descriptionOverflows ? (
              <button
                aria-controls={descriptionId}
                aria-expanded={descriptionExpanded}
                className="av-project-participation-card__description-toggle"
                onClick={() => setDescriptionExpanded(current => !current)}
                type="button"
              >
                {descriptionExpanded ? "See less" : "See more"}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="av-project-participation-card__actions">
        <Link
          className="av-app-button av-app-button--primary av-project-participation-card__action"
          to={`/app/project/${project.slug}`}
        >
          Open project
        </Link>
      </div>
    </ApplicationSurface>
  );
}
